import useFieldValidation, { isBlank } from '../../components/useFieldValidation';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, Building2, Check, ChevronDown, Globe, Hash, House, MapPin, Warehouse, X } from 'lucide-react';

const emptyForm = {
  code: '',
  name: '',
  address: '',
  city: '',
  country_id: '',
  capacity: '',
};

function formatCountry(country) {
  return country.name;
}

function optionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!React.isValidElement(node)) return '';
  return React.Children.toArray(node.props.children).map(optionText).join(' ');
}

function formFromWarehouse(warehouse) {
  if (!warehouse) return emptyForm;
  return {
    code: warehouse.code || '',
    name: warehouse.name || '',
    address: warehouse.address || '',
    city: warehouse.city || '',
    country_id: warehouse.country_id || '',
    capacity: warehouse.capacity == null ? '' : String(warehouse.capacity),
  };
}

function validateForm(form) {
  const errors = {};
  if (isBlank(form.code)) errors.code = 'Warehouse Code is required.';
  else if (form.code.trim().length > 30) errors.code = 'Warehouse Code must be 30 characters or fewer.';
  if (isBlank(form.name)) errors.name = 'Warehouse Name is required.';
  if (isBlank(form.address)) errors.address = 'Address is required.';
  if (isBlank(form.city)) errors.city = 'City is required.';
  if (isBlank(form.country_id)) errors.country = 'Country is required.';
  const capacity = Number(form.capacity);
  if (isBlank(form.capacity)) errors.capacity = 'Total Capacity is required.';
  else if (!Number.isFinite(capacity) || capacity <= 0) errors.capacity = 'Total Capacity must be greater than 0.';
  return errors;
}

function sanitizeCapacity(value) {
  const cleaned = String(value).replace(/[^0-9.]/g, '');
  const [whole, ...fraction] = cleaned.split('.');
  return fraction.length ? `${whole}.${fraction.join('').replace(/\./g, '')}` : whole;
}

function FieldSelect({ open, onToggle, onClose, onValidate, onSearchChange, value, placeholder, error, children, searchable = false, label }) {
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const options = searchable ? React.Children.toArray(children).filter(option => optionText(option).toLowerCase().includes(query.trim().toLowerCase())) : children;
  useEffect(() => {
    if (open && searchable) {
      setQuery('');
      onSearchChange?.('');
      searchRef.current?.focus({ preventScroll: true });
    }
  }, [open, searchable, onSearchChange]);
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) { onValidate?.(); onClose(); }
    }
    function handleKey(event) {
      if (event.key === 'Escape') { onValidate?.(); onClose(); }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, onValidate]);

  return <div className={`wh-form-select warehouse-country-select${error ? ' invalid' : ''}`} ref={ref} onClick={event => {
    if (searchable && event.target.closest('.wh-form-select-trigger') && !event.target.closest('input')) searchRef.current?.focus({ preventScroll: true });
    if (event.target.closest('[role="option"]')) ref.current?.querySelector(searchable ? 'input[role="combobox"]' : 'button[aria-haspopup="listbox"]')?.focus({ preventScroll: true });
  }} onBlur={event => { if (!ref.current?.contains(event.relatedTarget)) { onValidate?.(); if (open) onClose(); } }}>
    {searchable ? <div className={`wh-form-select-trigger${open ? ' open' : ''}${value ? '' : ' placeholder'}`}>
      <input ref={searchRef} className="warehouse-country-search" type="search" role="combobox" aria-label={`Search ${label.toLowerCase()}`} aria-controls={listId} aria-haspopup="listbox" aria-expanded={open} aria-autocomplete="list" autoComplete="off"
        value={open ? query : value || placeholder} placeholder={open ? `Search ${label.toLowerCase()}...` : undefined}
        onFocus={() => { if (!open) { setQuery(''); onToggle(); } }}
        onClick={() => { if (!open) { setQuery(''); onToggle(); } }}
        onChange={event => { setQuery(event.target.value); onSearchChange?.(event.target.value); if (!open) onToggle(); }} />
      <ChevronDown size={16} strokeWidth={1.8} className="wh-form-select-caret" />
    </div> : <button
      className={`wh-form-select-trigger${open ? ' open' : ''}${value ? '' : ' placeholder'}`}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={Boolean(error)}
      data-open-on-focus
      onMouseDown={event => { if (!open) event.preventDefault(); }}
      onFocus={event => { if (!open && !ref.current?.contains(event.relatedTarget)) onToggle(); }}
      onClick={() => { if (open) onValidate?.(); onToggle(); }}
    >
      <span className="wh-form-select-value">{value || placeholder}</span>
      <ChevronDown size={16} strokeWidth={1.8} className="wh-form-select-caret" />
    </button>}
    {open && <div className="wh-form-select-menu" id={listId} role="listbox" aria-labelledby={searchable ? 'wh-form-country-label' : undefined}>{options}{searchable && !options.length && <p className="wh-menu-empty">No matching countries.</p>}</div>}
  </div>;
}

export default function WarehouseModal({ mode, warehouse, countries, onClose, onSubmit }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => formFromWarehouse(warehouse));
  const { errors, setErrors, touchField } = useFieldValidation(form, validateForm);
  const [saving, setSaving] = useState(false);
  const [openSelect, setOpenSelect] = useState(null);
  const [countryQuery, setCountryQuery] = useState('');
  const titleId = 'wh-form-title';

  const selectedCountry = countries.find(country => country.id === form.country_id);
  const fallbackCountryName = countries.find(country => country.code === warehouse?.country)?.name
    || String(warehouse?.country || '').replace(/\s*\([A-Z]{2,3}\)\s*$/, '');
  const filteredCountries = countries.filter(country => formatCountry(country).toLowerCase().includes(countryQuery.trim().toLowerCase()));

  useEffect(() => {
    setForm(formFromWarehouse(warehouse));
    setErrors({});
    setOpenSelect(null);
    setCountryQuery('');
  }, [mode, warehouse, setErrors]);

  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape' && !saving && !openSelect) onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, openSelect, saving]);

  function updateField(name, value) {
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name === 'country_id' ? 'country' : name]: undefined }));
  }

  function closeSelect() {
    setOpenSelect(null);
    setCountryQuery('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        country_id: Number(form.country_id),
        capacity: Number(form.capacity),
        capacityUnit: 'm³',
      });
      onClose();
    } catch (error) {
      setErrors({ form: error.message });
    } finally {
      setSaving(false);
    }
  }

  return <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <form className="wh-form-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={handleSubmit}>
      <header className="wh-form-head">
        <span className="wh-form-head-icon" aria-hidden="true"><House size={18} strokeWidth={1.8} /></span>
        <div>
          <h2 id={titleId}>{isEdit ? 'Edit Warehouse' : 'Add Warehouse'}</h2>
          <p>{isEdit ? 'Update warehouse details' : 'Add a new warehouse to manage your logistics facilities'}</p>
        </div>
        <button className="wh-form-close" type="button" aria-label="Close" onClick={onClose} disabled={saving}><X size={16} strokeWidth={1.8} /></button>
      </header>

      <div className="wh-form-body">
        <label className="wh-form-field" htmlFor="wh-form-name">
          <span>Warehouse Name <em>*</em></span>
          <span className={`wh-form-control${errors.name ? ' invalid' : ''}`}>
            <Warehouse size={16} strokeWidth={1.7} />
            <input onBlur={() => touchField('name')} id="wh-form-name" value={form.name} placeholder="Enter warehouse name" aria-invalid={Boolean(errors.name)} autoFocus onChange={event => updateField('name', event.target.value)} />
          </span>
          {errors.name && <small className="wh-form-error">{errors.name}</small>}
        </label>

        <label className="wh-form-field" htmlFor="wh-form-code">
          <span>Warehouse Code <em>*</em></span>
          <span className={`wh-form-control${errors.code ? ' invalid' : ''}`}>
            <Hash size={16} strokeWidth={1.7} />
            <input onBlur={() => touchField('code')} id="wh-form-code" value={form.code} maxLength={30} placeholder="Enter warehouse code" aria-invalid={Boolean(errors.code)} onChange={event => updateField('code', event.target.value.toUpperCase())} />
          </span>
          {errors.code && <small className="wh-form-error">{errors.code}</small>}
        </label>

        <label className="wh-form-field" htmlFor="wh-form-address">
          <span>Address <em>*</em></span>
          <span className={`wh-form-control${errors.address ? ' invalid' : ''}`}>
            <MapPin size={16} strokeWidth={1.7} />
            <input onBlur={() => touchField('address')} id="wh-form-address" value={form.address} placeholder="Enter address" aria-invalid={Boolean(errors.address)} onChange={event => updateField('address', event.target.value)} />
          </span>
          {errors.address && <small className="wh-form-error">{errors.address}</small>}
        </label>

        <div className="wh-form-grid">
          <label className="wh-form-field" htmlFor="wh-form-city">
            <span>City <em>*</em></span>
            <span className={`wh-form-control${errors.city ? ' invalid' : ''}`}>
              <Building2 size={16} strokeWidth={1.7} />
              <input onBlur={() => touchField('city')} id="wh-form-city" value={form.city} placeholder="Enter city" aria-invalid={Boolean(errors.city)} onChange={event => updateField('city', event.target.value)} />
            </span>
            {errors.city && <small className="wh-form-error">{errors.city}</small>}
          </label>

          <div className="wh-form-field">
            <span id="wh-form-country-label">Country <em>*</em></span>
            <div className={`wh-form-control${errors.country ? ' invalid' : ''}`}>
              <Globe size={16} strokeWidth={1.7} />
              <FieldSelect
                open={openSelect === 'country'}
                onToggle={() => { setCountryQuery(''); setOpenSelect(openSelect === 'country' ? null : 'country'); }}
                onClose={closeSelect}
                value={selectedCountry ? formatCountry(selectedCountry) : fallbackCountryName}
                placeholder="Select country"
                label="Country"
                searchable
                onSearchChange={setCountryQuery}
                error={errors.country} onValidate={() => touchField('country')}
              >
                <div className="wh-menu-list">
                {filteredCountries.map(country => <button key={country.id} className={`wh-menu-item${form.country_id === country.id ? ' selected' : ''}`} type="button" role="option" aria-selected={form.country_id === country.id} onClick={() => { updateField('country_id', country.id); closeSelect(); }}>
                  <span>{formatCountry(country)}</span>
                  {form.country_id === country.id && <Check size={14} strokeWidth={2} />}
                </button>)}
                {!countries.length && <p className="wh-menu-empty">Countries are unavailable. Please reload and try again.</p>}
                {countries.length > 0 && !filteredCountries.length && <p className="wh-menu-empty">No matching countries.</p>}
                </div>
              </FieldSelect>
            </div>
            {errors.country && <small className="wh-form-error">{errors.country}</small>}
          </div>
        </div>

        <div className="wh-form-field">
          <label htmlFor="wh-form-capacity">Total Capacity <em>*</em></label>
          <div className="wh-form-capacity">
            <span className={`wh-form-control${errors.capacity ? ' invalid' : ''}`}>
              <Box size={16} strokeWidth={1.7} />
              <input onBlur={() => touchField('capacity')}
                id="wh-form-capacity"
                inputMode="decimal"
                value={form.capacity}
                placeholder="Enter capacity"
                aria-invalid={Boolean(errors.capacity)}
                onChange={event => updateField('capacity', sanitizeCapacity(event.target.value))}
              />
              <span className="wh-form-suffix">m³</span>
            </span>
          </div>
          <small className="wh-form-hint">Enter total storage capacity</small>
          {errors.capacity && <small className="wh-form-error">{errors.capacity}</small>}
        </div>

        {errors.form && <p className="wh-modal-error" role="alert">{errors.form}</p>}
      </div>

      <footer className="wh-form-footer">
        <button className="wh-form-cancel" type="button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="wh-form-save" type="submit" disabled={saving}>
          {saving ? 'Saving...' : <><Check size={15} strokeWidth={2.2} />{isEdit ? 'Save Changes' : 'Save Warehouse'}</>}
        </button>
      </footer>
    </form>
  </div>;
}
